import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, ArrowRight, MapPin } from 'lucide-react';
import PropertyCard from './PropertyCard';

/**
 * Horizontally-scrolling property rail for the homepage. Netflix-style:
 * snap-scroll on mobile, arrows on desktop. Renders nothing when the
 * `properties` array is empty so we never show a hollow strip.
 *
 * Props:
 *   title       — headline text
 *   subtitle    — small caption under the title
 *   properties  — array of Property objects
 *   viewAllHref — link for the "See all" CTA
 *   accent      — hex or tailwind colour used on the icon dot
 *   icon        — lucide icon component (optional)
 *   testId      — data-testid prefix for the rail
 */
const HomePropertyRail = ({
  title,
  subtitle,
  properties = [],
  viewAllHref,
  mapHref,
  accent = '#7042cc',
  icon: Icon,
  testId = 'home-rail',
  headerAction = null,
}) => {
  const scrollerRef = useRef(null);

  if (!properties || properties.length === 0) return null;

  const scrollBy = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    const card = el.querySelector('[data-rail-card]');
    const step = card ? card.getBoundingClientRect().width + 16 : 320;
    el.scrollBy({ left: dir * step * 2, behavior: 'smooth' });
  };

  return (
    <section
      className="pt-10 pb-6 px-4 sm:px-6 lg:px-8"
      data-testid={testId}
    >
      <div className="max-w-7xl mx-auto">
        <div className="flex items-end justify-between gap-3 mb-5">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span
                className="inline-block h-2 w-2 rounded-full"
                style={{ background: accent }}
              />
              <span className="text-[11px] font-semibold uppercase tracking-widest text-gray-500">
                {subtitle}
              </span>
            </div>
            <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 flex items-center gap-2">
              {Icon && <Icon size={20} className="text-gray-500" />}
              <span className="truncate">{title}</span>
            </h2>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {headerAction}
            <button
              type="button"
              onClick={() => scrollBy(-1)}
              aria-label="Scroll left"
              className="hidden sm:inline-flex items-center justify-center h-9 w-9 rounded-full border border-gray-200 bg-white hover:bg-gray-50 text-gray-600 transition"
              data-testid={`${testId}-scroll-left`}
            >
              <ChevronLeft size={18} />
            </button>
            <button
              type="button"
              onClick={() => scrollBy(1)}
              aria-label="Scroll right"
              className="hidden sm:inline-flex items-center justify-center h-9 w-9 rounded-full border border-gray-200 bg-white hover:bg-gray-50 text-gray-600 transition"
              data-testid={`${testId}-scroll-right`}
            >
              <ChevronRight size={18} />
            </button>
            {mapHref && (
              <Link
                to={mapHref}
                className="hidden md:inline-flex items-center gap-1 text-sm font-medium text-[#7042cc] hover:text-[#5b35a8] px-2.5 py-1 rounded-full ring-1 ring-[#7042cc]/25 hover:ring-[#7042cc]/50 bg-white transition"
                data-testid={`${testId}-view-map`}
                title="Browse these on a map"
              >
                <MapPin size={14} /> View on map
              </Link>
            )}
            {viewAllHref && (
              <Link
                to={viewAllHref}
                className="inline-flex items-center gap-1 text-sm font-medium text-[#7042cc] hover:text-[#5b35a8]"
                data-testid={`${testId}-view-all`}
              >
                See all <ArrowRight size={14} />
              </Link>
            )}
          </div>
        </div>

        <div
          ref={scrollerRef}
          className="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-3 -mx-4 px-4 sm:mx-0 sm:px-0 scroll-smooth [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          {properties.map((p) => (
            <div
              key={p._id || p.id}
              data-rail-card
              className="snap-start shrink-0 w-[78%] sm:w-[300px] md:w-[320px]"
            >
              <PropertyCard property={p} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default HomePropertyRail;
